/**
 * The telescope's note: two-operator FM at an irrational ratio, so the
 * partials never settle into a chord. It should sound like glass that has
 * been ground, not like a bell that has been cast.
 */
export class BellVoice {
  /** @param {import('../AudioEngine.js').AudioEngine} engine */
  constructor(engine) {
    this.engine = engine;
    this.ac = engine.ac;
  }

  play({ hz = 880, gain = 0.5, pan = 0, ratio = 2.41, dur = 5.0 } = {}) {
    const ac = this.ac;
    const now = ac.currentTime;

    const carrier = ac.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.value = hz;

    const mod = ac.createOscillator();
    mod.type = 'sine';
    mod.frequency.value = hz * ratio;

    const modDepth = ac.createGain();
    modDepth.gain.setValueAtTime(hz * 2.6, now);
    modDepth.gain.exponentialRampToValueAtTime(hz * 0.04, now + dur * 0.45);
    mod.connect(modDepth);
    modDepth.connect(carrier.frequency);

    const env = ac.createGain();
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(gain * 0.3, now + 0.012);
    env.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    // A high, almost inaudible partial that outlasts the strike.
    const air = ac.createOscillator();
    air.type = 'sine';
    air.frequency.value = hz * 5.03;
    const airGain = ac.createGain();
    airGain.gain.setValueAtTime(0.0001, now);
    airGain.gain.exponentialRampToValueAtTime(gain * 0.035, now + 0.3);
    airGain.gain.exponentialRampToValueAtTime(0.0001, now + dur * 1.3);
    air.connect(airGain);

    const panner = ac.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));

    carrier.connect(env);
    env.connect(panner);
    airGain.connect(panner);
    panner.connect(this.engine.dry);
    panner.connect(this.engine.send);

    carrier.start(now);
    mod.start(now);
    air.start(now);
    const end = now + dur * 1.4;
    carrier.stop(end);
    mod.stop(end);
    air.stop(end);
    carrier.onended = () => {
      panner.disconnect();
      env.disconnect();
      modDepth.disconnect();
      airGain.disconnect();
    };
  }
}
